<template>
  <fragment>
    <c-generic-section>
      <div class="container-xs">
        <c-section-header :data="header" class="center-content" />

        <p v-if="loading" class="doc-status">
          Loading&hellip;
          <span class="doc-zh">加载中</span>
        </p>
        <p v-else-if="loadFailed" class="doc-status">
          Content could not be loaded. Please try again later.
          <span class="doc-zh">内容加载失败,请稍后再试</span>
        </p>
        <p v-else-if="dataList.length === 0" class="doc-status">
          No content yet
          <span class="doc-zh">暂无内容</span>
        </p>

        <div v-else class="doc-list">
          <router-link
            v-for="item in dataList"
            :key="item.sys.id"
            class="doc-card"
            :to="{ path: '/detail', query: { type: type, id: item.sys.id, tag: item.sys.space.sys.id } }">
            <span class="doc-card-title">{{ item.fields.title }}</span>
            <span class="doc-card-date text-color-low">{{ formatDate(item.sys.createdAt) }}</span>
          </router-link>
        </div>

        <nav
          v-if="!loading && !loadFailed && all > 1"
          class="doc-pagination"
          aria-label="Pagination">
          <button
            type="button"
            class="doc-page-btn"
            :disabled="cur <= 1"
            @click="goTo(cur - 1)">Previous <span class="doc-btn-zh">上一页</span></button>
          <button
            v-for="index in indexs"
            :key="index"
            type="button"
            class="doc-page-btn"
            :class="{ 'is-active': cur === index }"
            @click="goTo(index)">{{ index }}</button>
          <button
            type="button"
            class="doc-page-btn"
            :disabled="cur >= all"
            @click="goTo(cur + 1)">Next <span class="doc-btn-zh">下一页</span></button>
          <span class="doc-page-count">{{ cur }} / {{ all }}</span>
        </nav>
      </div>
    </c-generic-section>

    <c-cta top-divider bottom-divider split />
  </fragment>
</template>

<script>
// import layout
import CLayout from '@/layouts/LayoutDefault.vue'
// import section header
import CSectionHeader from '@/components/sections/partials/SectionHeader.vue'
// import sections
import CGenericSection from '@/components/sections/GenericSection.vue'
import CCta from '@/components/sections/Cta.vue'

// News and Documentation entries live in the same Contentful space and are
// told apart by content type ("news" vs everything else). The token is a
// read-only Content Delivery token that has always shipped in the client
// bundle; the CI runner has no env setup, so it stays hardcoded here.
var CONTENTFUL_ENTRIES_URL =
  'https://cdn.contentful.com/spaces/tg92i1fck5ws/environments/master/entries?access_token=wVryCvO_gFJfPY34V4KTn9gDxZeEa3lNWHkra043a7c'

export default {
  name: 'Document',
  components: {
    CSectionHeader,
    CGenericSection,
    CCta
  },
  data() {
    return {
      allItems: [], // every entry returned by Contentful
      originalDataList: [], // entries matching the current mode
      dataList: [], // current page slice
      all: 0, // total pages
      cur: 1, // current page (1-based)
      count: 10, // entries per page
      type: 'N', // 'N' = News, 'H' = Documentation
      loading: true,
      loadFailed: false
    }
  },
  computed: {
    header: function () {
      if (this.type === 'H') {
        return {
          title: 'Documentation 资料文档',
          paragraph: 'Import guides, forms and reference material · 进口指南与常用资料'
        }
      }
      return {
        title: 'News 新闻动态',
        paragraph: 'Company news and industry updates · 公司动态与行业资讯'
      }
    },
    // Sliding window of up to five page numbers around the current page
    indexs: function () {
      var left = 1
      var right = this.all
      var pages = []
      if (this.all >= 5) {
        if (this.cur > 3 && this.cur < this.all - 2) {
          left = this.cur - 2
          right = this.cur + 2
        } else if (this.cur <= 3) {
          left = 1
          right = 5
        } else {
          left = this.all - 4
          right = this.all
        }
      }
      while (left <= right) {
        pages.push(left)
        left++
      }
      return pages
    }
  },
  created() {
    this.$emit('update:layout', CLayout)
    this.applyMode()
    this.fetchEntries()
  },
  watch: {
    $route() {
      this.applyMode()
    }
  },
  methods: {
    // Mode comes from the route query: a clean ?type=N|H, or the legacy bare
    // ?N / ?H keys still used by Header.vue and FooterNav.vue links.
    resolveType: function () {
      var query = this.$route.query || {}
      if (query.type === 'H' || query.type === 'N') {
        return query.type
      }
      if (Object.prototype.hasOwnProperty.call(query, 'H')) {
        return 'H'
      }
      if (Object.prototype.hasOwnProperty.call(query, 'N')) {
        return 'N'
      }
      return 'N'
    },
    applyMode: function () {
      this.type = this.resolveType()
      document.title =
        (this.type === 'H' ? 'Documentation 资料文档' : 'News 新闻动态') +
        ' — Line-1 Logistics'
      this.filterList()
    },
    fetchEntries: function () {
      this.loading = true
      this.loadFailed = false
      this.$axios
        .get(CONTENTFUL_ENTRIES_URL)
        .then((res) => {
          this.loading = false
          this.allItems = res.data && res.data.items ? res.data.items : []
          this.filterList()
        })
        .catch(() => {
          this.loading = false
          this.loadFailed = true
        })
    },
    filterList: function () {
      var wantNews = this.type === 'N'
      var filtered = []
      for (var i = 0; i < this.allItems.length; i++) {
        var isNews = this.allItems[i].sys.contentType.sys.id === 'news'
        if (isNews === wantNews) {
          filtered.push(this.allItems[i])
        }
      }
      this.originalDataList = filtered
      this.all = Math.ceil(filtered.length / this.count)
      this.cur = 1
      this.sliceCurrentPage()
    },
    // The Contentful query has no server-side paging, so pages are sliced
    // client-side from the filtered list.
    sliceCurrentPage: function () {
      this.dataList = this.originalDataList.slice(
        (this.cur - 1) * this.count,
        this.cur * this.count
      )
    },
    goTo: function (page) {
      if (page < 1 || page > this.all || page === this.cur) {
        return
      }
      this.cur = page
      this.sliceCurrentPage()
    },
    formatDate: function (value) {
      if (!value) {
        return ''
      }
      var date = new Date(value)
      if (isNaN(date.getTime())) {
        return ''
      }
      var month = date.getMonth() + 1
      var day = date.getDate()
      if (month < 10) month = '0' + month
      if (day < 10) day = '0' + day
      return date.getFullYear() + '-' + month + '-' + day
    }
  }
}
</script>

<style scoped>
.doc-status {
    text-align: center;
    margin: 32px 0;
}
.doc-zh {
    display: block;
    font-size: 13px;
    opacity: .7;
    margin-top: 6px;
}
.doc-list {
    text-align: left;
}
.doc-card {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    border: 1px solid rgba(128, 128, 128, .3);
    border-radius: 6px;
    padding: 20px 24px;
    margin-bottom: 12px;
    text-decoration: none;
    transition: border-color .15s ease;
}
.doc-card:hover {
    border-color: rgba(29, 95, 194, .5);
    text-decoration: none;
}
.doc-card-title {
    color: #1D5FC2;
    font-size: 18px;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-right: 16px;
}
.doc-card-date {
    flex: 0 0 auto;
    font-size: 14px;
}
.doc-pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 24px;
}
.doc-page-btn {
    border: 1px solid rgba(29, 95, 194, .35);
    background: transparent;
    color: #1D5FC2;
    border-radius: 6px;
    padding: 6px 14px;
    font-size: 14px;
    line-height: 1.4;
    cursor: pointer;
}
.doc-page-btn:hover:not(:disabled):not(.is-active) {
    background: #E3EDFB;
}
.doc-page-btn:disabled {
    opacity: .4;
    cursor: not-allowed;
}
.doc-page-btn.is-active {
    background: #1D5FC2;
    border-color: #1D5FC2;
    color: #fff;
    cursor: default;
}
.doc-btn-zh {
    font-size: 13px;
    opacity: .7;
    margin-left: 4px;
}
.doc-page-count {
    font-size: 13px;
    opacity: .7;
    margin-left: 4px;
}
</style>
